import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import moment from '../../../utilities/Moment'
import Panel from '../../Panel/Panel'
import PanelAction from '../../Panel/PanelAction'
import ButtonGroup from '../../Button/ButtonGroup'
import ButtonPanel from '../../Button/ButtonPanel'
import PowerSelect from '../../Form/PowerSelect'
import ReactSelectTheme from '../../Form/ReactSelectTheme'
import DateInput from '../../Form/DateInput'
import SettingToggle from '../SettingToggle'
import { Style } from '../../../styles'
import { AppState } from '../../../store'
import { EventTypePeriodType, SchedulingLimit, SchedulingLimitPeriod } from '../../../types'
import { TabProps } from './GeneralTab'

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Style.spacing.x1};

  &:not(:last-child) {
    margin-bottom: ${Style.spacing.x1};
  }
`

const Muted = styled.span`
  color: #637381;
`

const SmallField = styled.div<{ width?: string }>`
  width: ${props => props.width || '96px'};
  flex-shrink: 0;
`

const Spacer = styled.div`
  flex: 1;
`

const NoticeFields = styled.div`
  display: flex;
  gap: ${Style.spacing.x1};

  > *:first-child {
    flex: 1;
  }
`

const Hint = styled.p`
  color: #637381;
  margin-top: ${Style.spacing.x1};
`

const BUFFERS = [0, 5, 10, 15, 20, 30, 45, 60, 90, 120]
const INTERVALS = [5, 10, 15, 20, 30, 45, 60, 75, 90, 105, 120]
const PERIODS: SchedulingLimitPeriod[] = ['day', 'week', 'month', 'year']
const UNIT_FACTOR = { minutes: 1, hours: 60, days: 1440 } as const
// Explicit nulls so the API clears previously persisted periods
const NO_LIMIT: SchedulingLimit = { day: null, week: null, month: null, year: null }

interface Option { value: string, label: string }

// Keeps the raw string while typing; only valid integers >= min are reported, invalid input resets on blur
function IntInput({ value, min, onValid }: { value: number | null | undefined, min: number, onValid: (n: number) => void }) {
  const [raw, setRaw] = React.useState(value == null ? '' : String(value))
  React.useEffect(() => {
    setRaw(prev => (/^\d+$/.test(prev) && Number(prev) === value ? prev : value == null ? '' : String(value)))
  }, [value])

  return (
    <input
      type='number'
      min={min}
      value={raw}
      onChange={e => {
        const next = e.currentTarget.value
        setRaw(next)
        if (/^\d+$/.test(next) && Number(next) >= min) onValid(Number(next))
      }}
      onBlur={() => setRaw(value == null ? '' : String(value))}
    />
  )
}

function OptionSelect({ value, options, onChange }: { value: string, options: Option[], onChange: (v: string) => void }) {
  return (
    <PowerSelect
      options={options}
      value={options.find(option => option.value === value)}
      onChange={(option: Option) => option && onChange(option.value)}
      theme={ReactSelectTheme}
    />
  )
}

function LimitRows({ limit, onChange, unitLabel, defaultValue }: { defaultValue: number, limit: SchedulingLimit, onChange: (limit: SchedulingLimit) => void, unitLabel: (period: SchedulingLimitPeriod) => string }) {
  const { t } = useTranslation()
  const active = PERIODS.filter(p => limit[p] != null)
  const unused = PERIODS.filter(p => limit[p] == null)

  return (
    <>
      {active.map(period => (
        <Row key={period}>
          <SmallField>
            <IntInput min={1} value={limit[period]} onValid={n => onChange({ ...limit, [period]: n })} />
          </SmallField>
          <Muted>{unitLabel(period)}</Muted>
          <Spacer />
          {active.length > 1 && <PanelAction icon='trash' onClick={() => onChange({ ...limit, [period]: null })} />}
        </Row>
      ))}
      {unused.length > 0 && (
        <Row>
          <ButtonPanel icon='plus' text={t('Scheduling::Add limit')} onClick={() => onChange({ ...limit, [unused[0]]: defaultValue })} />
        </Row>
      )}
    </>
  )
}

const hasLimit = (limit: SchedulingLimit) => PERIODS.some(p => limit?.[p] != null)

export default function LimitsTab({ draft, onChange }: TabProps) {
  const { t } = useTranslation()
  const dateFormat = useSelector<AppState, string | undefined>(s => s.authentication.currentUser?.workspace?.setting?.date_format)
  const minutes = draft.minimum_booking_notice
  const storedUnit = (draft.minimum_booking_notice_unit || 'hours') as keyof typeof UNIT_FACTOR
  // Display in the stored unit only when it divides the stored minutes exactly; otherwise the largest exact unit
  const unit: keyof typeof UNIT_FACTOR = minutes % UNIT_FACTOR[storedUnit] === 0
    ? storedUnit
    : (['days', 'hours'] as const).find(u => minutes % UNIT_FACTOR[u] === 0) || 'minutes'
  const noticeValue = minutes / UNIT_FACTOR[unit]
  const withCurrent = (opts: Option[], current: number | null | undefined) =>
    current != null && current > 0 && !opts.some(o => o.value === String(current))
      ? [...opts, { value: String(current), label: t('Scheduling::{{count}} minutes', { count: current }) }].sort((a, b) => (a.value === 'default' ? -1 : b.value === 'default' ? 1 : Number(a.value) - Number(b.value)))
      : opts

  const baseBufferOptions = BUFFERS.map(b => ({ value: String(b), label: b === 0 ? t('Scheduling::No buffer time') : t('Scheduling::{{count}} minutes', { count: b }) }))
  const baseIntervalOptions = [{ value: 'default', label: t('Scheduling::Use event length (default)') }, ...INTERVALS.map(i => ({ value: String(i), label: t('Scheduling::{{count}} minutes', { count: i }) }))]
  const intervalOptions = withCurrent(baseIntervalOptions, draft.slot_interval)
  const beforeOptions = withCurrent(baseBufferOptions, draft.before_event_buffer)
  const afterOptions = withCurrent(baseBufferOptions, draft.after_event_buffer)
  const unitOptions = [
    { value: 'minutes', label: t('Scheduling::Minutes') },
    { value: 'hours', label: t('Scheduling::Hours') },
    { value: 'days', label: t('Scheduling::Days') },
  ]
  const perLabel = (period: SchedulingLimitPeriod) => ({
    day: t('Scheduling::per day'), week: t('Scheduling::per week'), month: t('Scheduling::per month'), year: t('Scheduling::per year'),
  })[period]
  const minutesPerLabel = (period: SchedulingLimitPeriod) => `${t('Scheduling::minutes')} ${perLabel(period)}`

  // react-datetime passes a Moment for a valid date and the raw string while typing; undefined means "ignore"
  const toDateString = (value: any): string | null | undefined => {
    if (value === '') return null
    const date = moment.isMoment(value) ? value : moment(value, dateFormat, true)
    return date.isValid() ? date.format('YYYY-MM-DD') : undefined
  }

  const onPeriodStartChange = (value: any) => {
    const start = toDateString(value)
    if (start === undefined) return
    onChange(start && draft.period_end_date && draft.period_end_date < start ? { period_start_date: start, period_end_date: null } : { period_start_date: start })
  }

  const onPeriodEndChange = (value: any) => {
    const end = toDateString(value)
    if (end === undefined) return
    onChange({ period_end_date: end && draft.period_start_date && end < draft.period_start_date ? null : end })
  }

  const onPeriodTypeChange = (periodType: EventTypePeriodType) => {
    onChange(periodType === EventTypePeriodType.RANGE
      ? { period_type: periodType }
      : { period_type: periodType, period_start_date: null, period_end_date: null })
  }

  return (
    <>
      <Panel>
        <div className='grid'>
          <div className='grid-cell with-6col'>
            <div className='form-item'>
              <label>{t('Scheduling::Before event')}</label>
              <OptionSelect value={String(draft.before_event_buffer)} options={beforeOptions} onChange={v => onChange({ before_event_buffer: Number(v) })} />
            </div>
          </div>
          <div className='grid-cell with-6col'>
            <div className='form-item'>
              <label>{t('Scheduling::After event')}</label>
              <OptionSelect value={String(draft.after_event_buffer)} options={afterOptions} onChange={v => onChange({ after_event_buffer: Number(v) })} />
            </div>
          </div>
          <div className='grid-cell with-6col'>
            <div className='form-item'>
              <label>{t('Scheduling::Minimum notice')}</label>
              <NoticeFields>
                <IntInput min={0} value={noticeValue}
                  onValid={n => onChange({ minimum_booking_notice: n * UNIT_FACTOR[unit], minimum_booking_notice_unit: unit })} />
                <SmallField width='128px'>
                  <OptionSelect value={unit} options={unitOptions}
                    onChange={v => {
                      const next = v as keyof typeof UNIT_FACTOR
                      onChange({ minimum_booking_notice_unit: next, minimum_booking_notice: noticeValue * UNIT_FACTOR[next] })
                    }} />
                </SmallField>
              </NoticeFields>
            </div>
          </div>
          <div className='grid-cell with-6col'>
            <div className='form-item'>
              <label>{t('Scheduling::Time-slot intervals')}</label>
              <OptionSelect value={draft.slot_interval ? String(draft.slot_interval) : 'default'} options={intervalOptions}
                onChange={v => onChange({ slot_interval: v === 'default' ? null : Number(v) })} />
            </div>
          </div>
        </div>
      </Panel>

      <SettingToggle id='limit-frequency' title={t('Scheduling::Limit booking frequency')}
        description={t('Scheduling::Limit how many times this event can be booked.')}
        checked={hasLimit(draft.booking_frequency_limit)}
        onCheckedChange={checked => onChange({ booking_frequency_limit: checked ? { ...NO_LIMIT, day: 1 } : NO_LIMIT })}>
        <LimitRows limit={draft.booking_frequency_limit} onChange={limit => onChange({ booking_frequency_limit: limit })} unitLabel={perLabel} defaultValue={1} />
      </SettingToggle>

      <SettingToggle id='limit-duration' title={t('Scheduling::Limit total booking duration')}
        description={t('Scheduling::Limit total amount of time that this event can be booked.')}
        checked={hasLimit(draft.booking_duration_limit)}
        onCheckedChange={checked => onChange({ booking_duration_limit: checked ? { ...NO_LIMIT, day: 60 } : NO_LIMIT })}>
        <LimitRows limit={draft.booking_duration_limit} onChange={limit => onChange({ booking_duration_limit: limit })} unitLabel={minutesPerLabel} defaultValue={60} />
      </SettingToggle>

      <SettingToggle id='limit-future' title={t('Scheduling::Limit future bookings')}
        description={t('Scheduling::Limit how far in the future this event can be booked.')}
        checked={draft.period_type !== EventTypePeriodType.UNLIMITED}
        onCheckedChange={checked => onChange(checked ? { period_type: EventTypePeriodType.ROLLING } : { period_type: EventTypePeriodType.UNLIMITED, period_start_date: null, period_end_date: null })}>
        <div className='form-item'>
          <ButtonGroup
            items={[
              { element: t('Scheduling::Rolling window'), active: draft.period_type === EventTypePeriodType.ROLLING, onClick: () => onPeriodTypeChange(EventTypePeriodType.ROLLING) },
              { element: t('Scheduling::Date range'), active: draft.period_type === EventTypePeriodType.RANGE, onClick: () => onPeriodTypeChange(EventTypePeriodType.RANGE) },
            ]}
          />
        </div>
        {draft.period_type === EventTypePeriodType.ROLLING ? (
          <Row>
            <SmallField>
              <IntInput min={1} value={draft.period_days} onValid={n => onChange({ period_days: n })} />
            </SmallField>
            <SmallField width='176px'>
              <OptionSelect value={draft.period_count_calendar_days ? 'calendar' : 'business'}
                options={[{ value: 'calendar', label: t('Scheduling::calendar days') }, { value: 'business', label: t('Scheduling::business days') }]}
                onChange={v => onChange({ period_count_calendar_days: v === 'calendar' })} />
            </SmallField>
            <Muted>{t('Scheduling::into the future')}</Muted>
          </Row>
        ) : (
          <>
            <div className='grid'>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('Scheduling::Start date')}</label>
                  <DateInput
                    name='period_start_date'
                    dateFormat={dateFormat}
                    timeFormat={false}
                    value={draft.period_start_date ? moment(draft.period_start_date) : undefined}
                    inputProps={{ placeholder: dateFormat }}
                    onChange={onPeriodStartChange}
                    closeOnSelect
                  />
                </div>
              </div>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('Scheduling::End date')}</label>
                  <DateInput
                    name='period_end_date'
                    dateFormat={dateFormat}
                    timeFormat={false}
                    value={draft.period_end_date ? moment(draft.period_end_date) : undefined}
                    inputProps={{ placeholder: dateFormat }}
                    isValidDate={(current) => !draft.period_start_date || !current.isBefore(moment(draft.period_start_date), 'day')}
                    onChange={onPeriodEndChange}
                    closeOnSelect
                  />
                </div>
              </div>
            </div>
            {(!draft.period_start_date || !draft.period_end_date) && (
              <Hint>{t('Scheduling::Choose both a start and an end date for the range.')}</Hint>
            )}
          </>
        )}
      </SettingToggle>

      <SettingToggle id='limit-per-booker' title={t('Scheduling::Limit number of upcoming bookings per booker')}
        description={t('Scheduling::Limit the number of active bookings a booker can make through this booking link.')}
        checked={draft.max_active_bookings_per_booker != null}
        onCheckedChange={checked => onChange({ max_active_bookings_per_booker: checked ? 1 : null })}>
        <Row>
          <SmallField>
            <IntInput min={1} value={draft.max_active_bookings_per_booker ?? 1} onValid={n => onChange({ max_active_bookings_per_booker: n })} />
          </SmallField>
          <Muted>{t('Scheduling::upcoming bookings')}</Muted>
        </Row>
      </SettingToggle>

      <SettingToggle id='first-slot' title={t('Scheduling::Show only the first available slot each day')}
        description={t('Scheduling::Limit to one slot per day at the earliest available time.')}
        checked={draft.only_first_slot_per_day}
        onCheckedChange={checked => onChange({ only_first_slot_per_day: checked })} />
    </>
  )
}
